import { useCallback, useEffect, useRef, useState } from "react";
import { useConvex, useMutation } from "convex/react";
import { CalendarClock, ImageUp, Trash2 } from "lucide-react";
import { api } from "../../convex/_generated/api";
import type { Id } from "../../convex/_generated/dataModel";
import { Badge, Button, Field, Input, Modal, Notice } from "./ui";
import { CATEGORY_LABELS, errorMessage, formatDateShort, formatMonthYear } from "../lib/format";
import { extractIdentifiers } from "../lib/identifiers";

/** Repositionne la date saisie à midi local, pour qu'un décalage horaire ne la fasse pas glisser d'un jour. */
function fromDateInput(value: string): number | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return undefined;
  const [year, month, day] = value.split("-").map(Number);
  return new Date(year, month - 1, day, 12, 0, 0).getTime();
}

/**
 * Prépare une capture pour la lecture : agrandie jusqu'à environ 2400 pixels de large et passée
 * en niveaux de gris avec un léger renfort de contraste. Le texte d'une feuille de calcul est
 * petit à l'écran ; lu tel quel, il produit des confusions de caractères (8/6, f/1, d/0) qu'un
 * simple agrandissement fait largement disparaître. En cas d'échec, l'image d'origine est lue.
 */
async function pretraiter(fichier: File): Promise<HTMLCanvasElement | File> {
  try {
    const bitmap = await createImageBitmap(fichier);
    const scale = bitmap.width >= 2400 ? 1 : Math.min(3, 2400 / bitmap.width);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    const contexte = canvas.getContext("2d");
    if (!contexte) return fichier;
    contexte.imageSmoothingEnabled = true;
    contexte.imageSmoothingQuality = "high";
    contexte.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close();
    const image = contexte.getImageData(0, 0, canvas.width, canvas.height);
    const pixels = image.data;
    for (let i = 0; i < pixels.length; i += 4) {
      const gris = 0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2];
      const contraste = Math.min(255, Math.max(0, (gris - 128) * 1.25 + 128));
      pixels[i] = pixels[i + 1] = pixels[i + 2] = contraste;
    }
    contexte.putImageData(image, 0, 0);
    return canvas;
  } catch {
    return fichier;
  }
}

type Correspondance = {
  _id: Id<"blacklist">;
  epicId?: string; epicIdNorm?: string;
  discordId?: string; discordIdNorm?: string;
  epicPseudo?: string; discordPseudo?: string;
  category: keyof typeof CATEGORY_LABELS;
  status: "active" | "lifted";
  addedAt: number;
  source: string;
};

/**
 * Datation par capture : coller une image d'écran — ou, mieux, les cellules copiées depuis
 * Google Sheets — retrouve les sanctions par identifiant exact et leur applique la date choisie.
 *
 * L'outil est fait pour ne pas pouvoir se tromper : seuls l'Epic ID et le Discord ID servent au
 * rapprochement, jamais les pseudos ; un caractère mal lu donne un identifiant introuvable,
 * jamais un autre joueur ; et rien n'est écrit avant relecture et confirmation.
 */
export function RedateModal({ onClose }: { onClose: () => void }) {
  const convex = useConvex();
  const redate = useMutation(api.blacklist.redateMany);
  const [dateStr, setDateStr] = useState("");
  const [statut, setStatut] = useState("");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [message, setMessage] = useState<{ tone: "error" | "success" | "warning"; text: string } | null>(null);
  const [matches, setMatches] = useState<Correspondance[]>([]);
  const [cochees, setCochees] = useState<Set<string>>(new Set());
  const [introuvables, setIntrouvables] = useState<string[]>([]);
  // Identifiant réel -> valeur lue sur la capture, pour marquer les lignes repêchées.
  const [corrections, setCorrections] = useState<Record<string, string>>({});
  const [dragOver, setDragOver] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const traiterTexte = useCallback(async (texte: string) => {
    const { epicIds, discordIds } = extractIdentifiers(texte);
    if (epicIds.length === 0 && discordIds.length === 0) {
      setMessage({ tone: "error", text: "Aucun identifiant reconnu — il faut un Epic ID (32 caractères) ou un Discord ID (15 à 21 chiffres)." });
      return;
    }
    setStatut(`${epicIds.length + discordIds.length} identifiants lus, recherche dans la base…`);
    const resultat = await convex.query(api.blacklist.findByIdentifiers, { epicIds, discordIds });
    const trouves = resultat.entries;
    // Les collages successifs s'accumulent : plusieurs captures peuvent partager la même date.
    setMatches((courantes) => {
      const parId = new Map(courantes.map((entry) => [entry._id as string, entry]));
      for (const entry of trouves) parId.set(entry._id as string, entry as Correspondance);
      return [...parId.values()];
    });
    setCochees((courantes) => {
      const suivantes = new Set(courantes);
      for (const entry of trouves) suivantes.add(entry._id as string);
      return suivantes;
    });
    // Le serveur a pu repêcher un identifiant mal lu : la ligne est marquée « lecture
    // corrigée » pour que la relecture s'attarde dessus.
    setCorrections((courantes) => {
      const suivantes = { ...courantes };
      for (const correction of resultat.corrections) suivantes[correction.matched] = correction.input;
      return suivantes;
    });
    // Un identifiant ni retrouvé ni repêché est signalé sans rien modifier : soit la ligne
    // n'existe pas, soit la lecture a trop dévié pour être corrigée sans risque.
    const connus = new Set<string>();
    for (const entry of trouves) {
      if (entry.epicIdNorm) connus.add(entry.epicIdNorm);
      if (entry.discordIdNorm) connus.add(entry.discordIdNorm);
    }
    const corriges = new Set(resultat.corrections.map((c) => c.input.toLowerCase()));
    const manquants = [...epicIds, ...discordIds]
      .filter((id) => !connus.has(id.toLowerCase()) && !corriges.has(id.toLowerCase()));
    setIntrouvables((courants) => [...new Set([...courants, ...manquants])]);
    setStatut("");
  }, [convex]);

  const traiterImage = useCallback(async (fichier: File) => {
    setBusy(true); setMessage(null);
    try {
      setStatut("Chargement du module de lecture… (téléchargé au premier usage)");
      // Chargé à la demande : le module de reconnaissance pèse plusieurs mégaoctets et la
      // plupart des sessions n'en ont pas besoin.
      const Tesseract = await import("tesseract.js");
      const worker = await Tesseract.createWorker("eng", 1, {
        logger: (m: { status: string; progress: number }) => {
          if (m.status === "recognizing text") setStatut(`Lecture de l’image… ${Math.round(m.progress * 100)} %`);
        },
      });
      try {
        const source = await pretraiter(fichier);
        const { data } = await worker.recognize(source);
        await traiterTexte(data.text);
      } finally {
        await worker.terminate();
      }
    } catch (reason) {
      setMessage({ tone: "error", text: errorMessage(reason) || "La lecture de l’image a échoué." });
      setStatut("");
    } finally {
      setBusy(false);
    }
  }, [traiterTexte]);

  const recevoir = useCallback((fichier: File | null, texte: string) => {
    setMessage(null);
    if (fichier) { void traiterImage(fichier); return; }
    if (!texte.trim()) return;
    setBusy(true);
    void traiterTexte(texte)
      .catch((reason) => { setMessage({ tone: "error", text: errorMessage(reason) }); setStatut(""); })
      .finally(() => setBusy(false));
  }, [traiterImage, traiterTexte]);

  // Ctrl+V fonctionne n'importe où dans la fenêtre : une image d'écran comme des cellules
  // copiées depuis Google Sheets — cette seconde voie est exacte, sans lecture d'image.
  useEffect(() => {
    const surColler = (event: ClipboardEvent) => {
      const data = event.clipboardData;
      if (!data) return;
      const image = Array.from(data.items).find((item) => item.type.startsWith("image/"))?.getAsFile() ?? null;
      const texte = data.getData("text");
      if (image || texte.trim()) { event.preventDefault(); recevoir(image, texte); }
    };
    window.addEventListener("paste", surColler);
    return () => window.removeEventListener("paste", surColler);
  }, [recevoir]);

  const vider = () => { setMatches([]); setCochees(new Set()); setIntrouvables([]); setCorrections({}); setMessage(null); setStatut(""); };

  const appliquer = async () => {
    const addedAt = fromDateInput(dateStr);
    if (!addedAt) { setMessage({ tone: "error", text: "Choisissez d’abord la date de sanction à appliquer." }); return; }
    const ids = matches.filter((entry) => cochees.has(entry._id as string)).map((entry) => entry._id);
    if (ids.length === 0) return;
    setApplying(true); setMessage(null);
    try {
      const { updated } = await redate({ ids, addedAt });
      setMessage({ tone: "success", text: `${updated} sanction${updated > 1 ? "s" : ""} datée${updated > 1 ? "s" : ""} au ${formatDateShort(addedAt)}. Collez la capture suivante.` });
      vider();
    } catch (reason) {
      setMessage({ tone: "error", text: errorMessage(reason) });
    } finally {
      setApplying(false);
    }
  };

  const basculer = (id: string, coche: boolean) => setCochees((courantes) => {
    const suivantes = new Set(courantes);
    if (coche) suivantes.add(id); else suivantes.delete(id);
    return suivantes;
  });

  return (
    <Modal
      className="modal--wide"
      title="Dater des sanctions par capture"
      description="Collez une capture d’écran ou des cellules copiées : les joueurs retrouvés par identifiant exact reçoivent la date choisie. Rien n’est modifié avant votre confirmation."
      onClose={onClose}
    >
      <Field label="Date de la sanction" hint="Appliquée à toutes les lignes cochées ci-dessous.">
        <Input type="date" value={dateStr} max={new Date().toISOString().slice(0, 10)} onChange={(event) => setDateStr(event.target.value)} />
      </Field>

      <label
        className={`dropzone${dragOver ? " dropzone--over" : ""}${busy ? " dropzone--busy" : ""}`}
        onDragOver={(event) => { event.preventDefault(); if (!busy) setDragOver(true); }}
        onDragEnter={(event) => { event.preventDefault(); if (!busy) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragOver(false);
          if (busy) return;
          const fichier = event.dataTransfer.files?.[0] ?? null;
          const texte = event.dataTransfer.getData("text");
          if (fichier && fichier.type.startsWith("image/")) recevoir(fichier, "");
          else if (texte.trim()) recevoir(null, texte);
          else if (fichier) setMessage({ tone: "error", text: `« ${fichier.name} » n’est pas une image.` });
        }}
      >
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          disabled={busy}
          className="dropzone__input"
          onChange={(event) => {
            const fichier = event.target.files?.[0];
            if (fichier) recevoir(fichier, "");
            event.target.value = "";
          }}
        />
        <span className="dropzone__icon"><ImageUp size={22} aria-hidden="true" /></span>
        <span className="dropzone__copy">
          {busy && statut ? <strong>{statut}</strong> : (
            <>
              <strong>Collez (Ctrl+V) ou déposez une capture d’écran</strong>
              <small>Encore plus sûr : copiez les cellules dans Google Sheets et collez-les ici — lecture exacte, sans image.</small>
            </>
          )}
        </span>
      </label>

      {message && <Notice tone={message.tone}>{message.text}</Notice>}
      {Object.keys(corrections).length > 0 && matches.length > 0 && (
        <Notice tone="info">
          {Object.keys(corrections).length} identifiant{Object.keys(corrections).length > 1 ? "s" : ""} mal
          lu{Object.keys(corrections).length > 1 ? "s" : ""} sur la capture, corrigé{Object.keys(corrections).length > 1 ? "s" : ""} par
          rapprochement avec la base — vérifiez les lignes « Lecture corrigée ».
        </Notice>
      )}
      {introuvables.length > 0 && (
        <Notice tone="warning">
          {introuvables.length} identifiant{introuvables.length > 1 ? "s" : ""} lu{introuvables.length > 1 ? "s" : ""} mais absent{introuvables.length > 1 ? "s" : ""} de la base :{" "}
          <span className="mono-wrap">{introuvables.slice(0, 4).join(", ")}{introuvables.length > 4 ? "…" : ""}</span>.
          Vérifiez la netteté de la capture ou copiez plutôt les cellules en texte.
        </Notice>
      )}

      {matches.length > 0 && (
        <>
          <div className="redate-head">
            <span><strong>{cochees.size}</strong> / {matches.length} sanction{matches.length > 1 ? "s" : ""} recevron{cochees.size > 1 ? "t" : "a"} la date</span>
            <Button variant="ghost" onClick={vider}><Trash2 size={14} /> Vider</Button>
          </div>
          <div className="table-scroll redate-table">
            <table>
              <thead>
                <tr>
                  <th scope="col" className="col-select">
                    <input
                      type="checkbox"
                      aria-label="Tout cocher"
                      checked={matches.length > 0 && matches.every((entry) => cochees.has(entry._id as string))}
                      onChange={(event) => setCochees(event.target.checked ? new Set(matches.map((entry) => entry._id as string)) : new Set())}
                    />
                  </th>
                  <th scope="col">Joueur</th>
                  <th scope="col">Sanction</th>
                  <th scope="col">Date actuelle</th>
                </tr>
              </thead>
              <tbody>
                {matches.map((entry) => (
                  <tr key={entry._id}>
                    <td className="col-select">
                      <input
                        type="checkbox"
                        aria-label={`Dater ${entry.epicPseudo || entry.discordPseudo || "cette entrée"}`}
                        checked={cochees.has(entry._id as string)}
                        onChange={(event) => basculer(entry._id as string, event.target.checked)}
                      />
                    </td>
                    <td>
                      <div className="identity-cell">
                        <strong>{entry.epicPseudo || entry.discordPseudo || "Sans pseudo"}</strong>
                        {entry.epicId && <span className="mono-wrap">{entry.epicId}</span>}
                        {entry.discordId && <small className="mono-wrap">{entry.discordId}</small>}
                        {(() => {
                          const lu = (entry.epicIdNorm && corrections[entry.epicIdNorm])
                            || (entry.discordIdNorm && corrections[entry.discordIdNorm]);
                          return lu
                            ? <Badge tone="warning" title={`Lu « ${lu} » sur la capture ; un seul identifiant de la base est aussi proche. Vérifiez le pseudo avant de confirmer.`}>Lecture corrigée</Badge>
                            : null;
                        })()}
                      </div>
                    </td>
                    <td>
                      <div className="status-cell">
                        <Badge tone="rose">{CATEGORY_LABELS[entry.category]}</Badge>
                        {entry.status === "lifted" && <Badge tone="success">Levée</Badge>}
                      </div>
                    </td>
                    <td><span className="nowrap" title={formatDateShort(entry.addedAt)}>{formatMonthYear(entry.addedAt)}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div className="modal-actions">
        <Button variant="secondary" onClick={onClose}>Fermer</Button>
        <Button loading={applying} disabled={busy || cochees.size === 0 || !dateStr} onClick={() => void appliquer()}>
          <CalendarClock size={15} /> Dater {cochees.size > 0 ? cochees.size : ""} sanction{cochees.size > 1 ? "s" : ""}
        </Button>
      </div>
    </Modal>
  );
}
